import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/use-auth-compat';
import {
  listExamTargets,
  addExamTarget,
  updateExamTarget,
  removeExamTarget,
  type AddExamTargetInput,
} from '@/services/examTargetService';
import type { StudentExamTarget } from '@/types/registeredStudent';

const QUERY_KEY = ['examTargets'] as const;

export function useExamTargets() {
  const { user } = useAuth();
  const token = user?.access_token ?? '';
  const qc = useQueryClient();

  const query = useQuery<StudentExamTarget[]>({
    queryKey: QUERY_KEY,
    enabled: !!token,
    queryFn: () => listExamTargets(token),
  });

  const addMutation = useMutation({
    mutationFn: (input: AddExamTargetInput) => addExamTarget(token, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<AddExamTargetInput> }) =>
      updateExamTarget(token, id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => removeExamTarget(token, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  return {
    targets: query.data ?? [],
    isLoading: query.isLoading,
    add: addMutation.mutateAsync,
    update: updateMutation.mutateAsync,
    remove: removeMutation.mutateAsync,
    refresh: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  };
}
